import { type FormEvent, useState } from 'react'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { Logo } from '@/teile/Logo'
import { ApiError } from '../api/client.js'
import { anmelden } from '../api/partner.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Anmeldung an der Partner-Oberfläche.
 *
 * Anders als bei den Anwendern gibt es hier ein Passwort: Ein Partner-Admin
 * sitzt an keinem gekoppelten Gerät (ADR-0004 gilt für Anwender). Das erste
 * Konto legt netable an, danach der Partner selbst (`Projektstruktur.md` §3.0)
 * — ein offenes Registrierungsformular gibt es deshalb nicht.
 */
export function Anmelden() {
  const { neuLaden } = useSitzung()
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const formular = new FormData(ereignis.currentTarget)

    setLaeuft(true)
    setMeldung(null)
    try {
      await anmelden(String(formular.get('email') ?? ''), String(formular.get('passwort') ?? ''))
      // Erst die Sitzung entscheidet: Ein Konto mit gültigem Passwort ist noch
      // kein Partner-Konto. Antwortet der Server darauf mit 401, bleibt das
      // Formular stehen — mit derselben Meldung wie bei falschem Passwort.
      await neuLaden()
    } catch (fehler) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 0
          ? fehler.message
          : 'Anmeldung nicht möglich. Bitte E-Mail und Passwort prüfen.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center p-8">
      {/* Das Logo haengt ueber dem Block und zaehlt fuer die Mitte nicht mit:
          Das Formular bleibt mittig, das Logo steht darueber. */}
      <div className="relative flex flex-col gap-7">
        <Logo className="absolute bottom-full left-1/2 mb-8 w-[23.5rem] max-w-none -translate-x-1/2 [@media(min-height:880px)]:w-[30.5rem]" />

        <form onSubmit={absenden} className="flex flex-col gap-4">
          <Input
            isRequired
            name="email"
            type="email"
            label="E-Mail"
            autoComplete="username"
            size="md"
          />
          <Input
            isRequired
            name="passwort"
            type="password"
            label="Passwort"
            autoComplete="current-password"
            size="md"
          />

          {/* Eine Meldung für alle Fälle: falsche E-Mail, falsches Passwort,
            gesperrtes Konto, kein Partner-Konto. Wer hier unterscheidet,
            verrät, welche Adressen es gibt (SECURITY.md §2). */}
          {meldung ? (
            <p role="status" className="rounded-lg bg-error-primary p-3 text-sm text-error-primary">
              {meldung}
            </p>
          ) : null}

          <Button type="submit" size="lg" isLoading={laeuft}>
            Anmelden
          </Button>
        </form>

        <p className="text-xs text-tertiary">
          Zugänge vergibt Ihr Partner-Administrator. Ein vergessenes Passwort wird zurückgesetzt,
          nicht wiederhergestellt.
        </p>
      </div>
    </main>
  )
}
